import type { HttpInterceptorFn } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { tap } from 'rxjs';

import { ApiError } from '../error/api-error';
import { projectIdFromApiUrl } from './project-scope';

/** Codes a project-scoped mount answers with when the id is not a project of the workspace. */
const PROJECT_NOT_FOUND_CODES: ReadonlySet<string> = new Set([
  'SCHEDULER_PROJECT_NOT_FOUND',
  'PROJECT_NOT_FOUND',
]);

/**
 * Project ids the backend has refused as unknown during this session, so the
 * project page can show "project not found" even when the workspace list
 * (fetched earlier) still had the project.
 */
@Injectable({ providedIn: 'root' })
export class MissingProjects {
  private readonly ids = signal<ReadonlySet<string>>(new Set());

  has(id: string): boolean {
    return this.ids().has(id);
  }

  mark(id: string): void {
    if (!this.ids().has(id)) {
      this.ids.update((current) => new Set([...current, id]));
    }
  }
}

/**
 * Notes a `404 …PROJECT_NOT_FOUND` from a `/projects/{id}/<app>/…` call.
 * Sits outside `errorInterceptor`, so the error is already an `ApiError`;
 * it is passed on untouched for the caller to handle as usual.
 */
export const projectNotFoundInterceptor: HttpInterceptorFn = (request, next) => {
  const projectId = projectIdFromApiUrl(request.url);

  if (!projectId) {
    return next(request);
  }

  const missing = inject(MissingProjects);

  return next(request).pipe(
    tap({
      error: (error: unknown) => {
        if (ApiError.isApiError(error) && PROJECT_NOT_FOUND_CODES.has(error.code ?? '')) {
          missing.mark(projectId);
        }
      },
    }),
  );
};
